import React, { Fragment, useEffect, useMemo } from "react";

import { Link } from "@mui/material";
import clsx from "clsx";
import appConfig from "config/appConfig";
import { Trans, useTranslation } from "react-i18next";

import { Link as RouterLink } from "react-router-dom";

import AgeIcon from "assets/icons/outlined/18.svg?react";
import LogoUrl from "assets/images/logo-dice.webp";

import { getPaths } from "core/getPaths";
import { useGames, useSystem } from "shared/hooks";

import { GameCategories } from "shared/types/GameModel";
import { generateUniqueKey } from "utils/serviceUtils/helpers";

import styles from "./Footer.module.scss";

const { games: gamesRoutes } = getPaths();

const Footer: React.FC = () => {
	const { t } = useTranslation();

	const { getData: getSystemData } = useSystem();
	const { bootstrap } = getSystemData();

	const { getData: getGamesData } = useGames();
	const { categories } = getGamesData();

	useEffect(() => {
		try {
			// @ts-expect-error: proper variable
			anj_7d68a183_0cf3_48c2_82bc_ee1c6fbd45a1?.init();
		} catch (err) {
			console.log("Error: ", err);
		}
	}, []);

	const blocks: {
		id: string;
		label: string;
		items: { id: string; label: string; inactive?: boolean; link?: string; externalLink?: string }[];
	}[] = useMemo(() => {
		const slotsCategory = categories.find(x => x.menuId === GameCategories.SLOTS);
		const tableCategory = categories.find(x => x.menuId === GameCategories.TABLEGAMES);
		const liveCategory = categories.find(x => x.menuId === GameCategories.LIVECASINO);

		return [
			{
				id: generateUniqueKey(),
				label: t("about"),
				items: [
					{
						id: generateUniqueKey(),
						label: t("chips_token"),
						externalLink: appConfig.GLOBAL_CONSTANTS.CHIPS_PROTOCOL_URL
					},
					{
						id: generateUniqueKey(),
						label: t("responsible_gaming"),
						externalLink: appConfig.GLOBAL_CONSTANTS.RESPONSIBLE_GAMING_URL
					},
					{ id: generateUniqueKey(), label: t("kyc_policy"), externalLink: appConfig.GLOBAL_CONSTANTS.KYC_POLICY_URL },
					{ id: generateUniqueKey(), label: t("aml_policy"), externalLink: appConfig.GLOBAL_CONSTANTS.AML_POLICY_URL },
					{
						id: generateUniqueKey(),
						label: t("privacy_policy"),
						externalLink: appConfig.GLOBAL_CONSTANTS.PRIVACY_POLICY_URL
					},
					// id: generateUniqueKey(), { label: t("terms_and_conditions"), externalLink: memberRoutes.termsOfService.getPath() }
					{
						id: generateUniqueKey(),
						label: t("terms_of_service"),
						externalLink: appConfig.GLOBAL_CONSTANTS.TERMS_OF_SERVICE_URL
					}
				]
			},
			// {
			// 	label: t("exchange"),
			// 	items: [
			// 		{ label: t("buy_crypto"), inactive: true },
			// 		{ label: t("spot_trading"), inactive: true },
			// 		{ label: t("futures_trading"), inactive: true },
			// 		{ label: t("Earn"), inactive: true }
			// 	]
			// },
			{
				id: generateUniqueKey(),
				label: t("gaming"),
				items: [
					// { id: generateUniqueKey(), label: t("vip_program"), inactive: true }, // hide as we don't have such category
					slotsCategory
						? {
								id: generateUniqueKey(),
								label: t("slots"),
								link: `${gamesRoutes.category.getPath()}/${slotsCategory.ID}`
							}
						: undefined!,
					tableCategory
						? {
								id: generateUniqueKey(),
								label: t("table"),
								link: `${gamesRoutes.category.getPath()}/${tableCategory.ID}`
							}
						: undefined!,
					liveCategory
						? {
								id: generateUniqueKey(),
								label: t("live"),
								link: `${gamesRoutes.category.getPath()}/${liveCategory.ID}`
							}
						: undefined!,
					{ id: generateUniqueKey(), label: t("sports"), externalLink: appConfig.GLOBAL_CONSTANTS.DIGITAIN_SPORTS_URL }
					// { id: generateUniqueKey(), label: t("tournaments"), link: memberRoutes.tournaments.getPath() }
				].filter(Boolean)
			},
			{
				id: generateUniqueKey(),
				label: t("community"),
				items: [
					{ id: generateUniqueKey(), label: "X", externalLink: appConfig.GLOBAL_CONSTANTS.SOCIAL_X_URL },
					{ id: generateUniqueKey(), label: "Telegram", externalLink: appConfig.GLOBAL_CONSTANTS.SOCIAL_TELEGRAM_URL },
					// { label: "Discord" },
					{ id: generateUniqueKey(), label: "Instagram", externalLink: appConfig.GLOBAL_CONSTANTS.SOCIAL_INSTAGRAM_URL }
					// { label: "Facebook" }
				]
			},
			{
				id: generateUniqueKey(),
				label: t("support"),
				items: [
					{ id: generateUniqueKey(), label: t("docs"), externalLink: appConfig.GLOBAL_CONSTANTS.DOCS_URL },
					{ id: generateUniqueKey(), label: t("faqs"), externalLink: appConfig.GLOBAL_CONSTANTS.FAQ_URL }
					// { label: t("affiliate"), inactive: true },
					// { label: t("referral"), inactive: true }
				]
			}
		];
	}, [t, categories]);

	return (
		<footer className="w-full bg-red-dark rounded-2xl px-4 py-[18px] sm:p-6">
			<div className="sm:flex sm:justify-between">
				<div className="sm:w-[316px]">
					<img src={LogoUrl} alt="Logo" className={styles.footer__logo} />
					{!!bootstrap?.footerText?.en ? (
						<p className={styles.footer__desc}>{bootstrap.footerText.en}</p>
					) : (
						<p
							className={styles.footer__desc}
							dangerouslySetInnerHTML={{
								__html: t("blockasino_company_desc")
							}}
						/>
					)}
					<p className={styles.footer__subdesc}>{t("trading_and_gambling")}</p>
				</div>
				<div className={styles.footer__navsWrapper}>
					{blocks.map(block => (
						<div key={block.id} className={styles.footer__navBlock}>
							<h4 className={styles.footer__navTitle}>{block.label}</h4>
							<div className={styles.footer__navBlockBody}>
								{block.items.map(item => (
									<Fragment key={item.id}>
										{item?.link || item?.externalLink ? (
											<Link
												to={item.link || item.externalLink!}
												target={item?.externalLink ? "_blank" : undefined}
												component={RouterLink}
												className="no-underline w-full cursor-pointer"
											>
												<p className={clsx(styles.footer__navText)}>{item.label}</p>
											</Link>
										) : (
											<p className={clsx(styles.footer__navText)}>{item.label}</p>
										)}
									</Fragment>
								))}
							</div>
						</div>
					))}
				</div>
			</div>
			<div className={styles.footer__copyrightBlock}>
				<div className="flex items-center gap-4 sm:gap-3 max-sm:flex-col">
					<div className="flex items-center justify-center gap-4 sm:gap-3">
						<div
							id="anj-7d68a183-0cf3-48c2-82bc-ee1c6fbd45a1"
							data-anj-seal-id="7d68a183-0cf3-48c2-82bc-ee1c6fbd45a1"
							data-anj-image-size="32"
							data-anj-image-type="basic-small"
						></div>
						<AgeIcon className="w-8 svg-paths:fill-white" />
					</div>
					<p className="font-medium text-xs leading-4 text-white max-sm:text-center">
						<Trans
							i18nKey="site_is_protected_desc"
							components={[
								<a
									key={"footer-google-privacy-policy-link"}
									href={appConfig.GOOGLE_RECAPTCHA_PRIVACY_POLICY_LINK}
									target="_blank"
									rel="noopener noreferrer"
									className="underline underline-offset-2"
								/>,
								<a
									key={"footer-terms-link"}
									href={appConfig.GLOBAL_CONSTANTS.TERMS_OF_SERVICE_URL}
									target="_blank"
									rel="noopener noreferrer"
									className="underline underline-offset-2"
								/>
							]}
						/>
					</p>
				</div>
				<div className="flex max-sm:flex-col justify-center items-center gap-4 sm:gap-3">
					<a href={`mailto:${appConfig.GLOBAL_CONSTANTS.SUPPORT_EMAIL}`} className={styles.footer__copyrightText}>
						{appConfig.GLOBAL_CONSTANTS.SUPPORT_EMAIL}
					</a>
					<p className={styles.footer__copyrightText}>{t("copyright")}</p>
				</div>
			</div>
		</footer>
	);
};

export default Footer;
